/**
 * Real-Time Updates Service
 * WebSocket connection management with TanStack Query integration
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { decisionKeys } from './decisions';
import { matterKeys } from './matters';
import { briefingKeys } from './briefings';

// Types
export type RealtimeEventType =
  | 'decision.created'
  | 'decision.updated'
  | 'decision.deleted'
  | 'matter.updated'
  | 'matter.stage_changed'
  | 'briefing.generated'
  | 'notification'
  | 'sync';

export interface RealtimeEvent<T = unknown> {
  type: RealtimeEventType;
  payload: T;
  timestamp: Date;
  userId?: string;
}

export interface RealtimeConfig {
  url?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

type RealtimeStatus = 'connecting' | 'connected' | 'disconnected' | 'error';
type EventHandler = (event: RealtimeEvent) => void;

// WebSocket Manager
class RealtimeManager {
  private ws: WebSocket | null = null;
  private config: Required<RealtimeConfig>;
  private reconnectAttempts = 0;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private status: RealtimeStatus = 'disconnected';
  private handlers: Set<EventHandler> = new Set();

  constructor(config: RealtimeConfig = {}) {
    this.config = {
      url: config.url ?? import.meta.env.VITE_WEBSOCKET_URL ?? 'wss://us-central1-lawity-gl-2025.cloudfunctions.net',
      reconnectInterval: config.reconnectInterval ?? 3000,
      maxReconnectAttempts: config.maxReconnectAttempts ?? 10,
      heartbeatInterval: config.heartbeatInterval ?? 30000,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.status = 'connecting';
    this.notifyStatusChange();

    try {
      // In development, simulate WebSocket with mock events
      if (import.meta.env.DEV) {
        this.simulateMockConnection();
        return;
      }

      this.ws = new WebSocket(this.config.url);

      this.ws.onopen = () => {
        this.status = 'connected';
        this.reconnectAttempts = 0;
        this.notifyStatusChange();
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RealtimeEvent;
          this.notifyHandlers(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = () => {
        this.status = 'error';
        this.notifyStatusChange();
      };

      this.ws.onclose = () => {
        this.status = 'disconnected';
        this.notifyStatusChange();
        this.stopHeartbeat();
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      this.status = 'error';
      this.notifyStatusChange();
      this.attemptReconnect();
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.status = 'disconnected';
    this.notifyStatusChange();
  }

  subscribe(handler: EventHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  private simulateMockConnection(): void {
    // Simulate connected state after a short delay
    setTimeout(() => {
      this.status = 'connected';
      this.notifyStatusChange();

      // Simulate periodic mock events
      this.startMockEventEmitter();
    }, 500);
  }

  private mockEventTimer: NodeJS.Timeout | null = null;

  private startMockEventEmitter(): void {
    // Emit mock events every 30-60 seconds to simulate activity
    const emitMockEvent = () => {
      const events: RealtimeEvent[] = [
        {
          type: 'decision.created',
          payload: {
            id: `mock-${Date.now()}`,
            type: 'task',
            priority: 'normal',
            title: 'New task arrived',
          },
          timestamp: new Date(),
        },
        {
          type: 'notification',
          payload: {
            message: 'Client uploaded a new document',
            matterId: 'matter-1',
          },
          timestamp: new Date(),
        },
        {
          type: 'matter.updated',
          payload: {
            id: 'matter-1',
            field: 'lastActivity',
            value: new Date(),
          },
          timestamp: new Date(),
        },
      ];

      const randomEvent = events[Math.floor(Math.random() * events.length)];
      this.notifyHandlers(randomEvent);

      // Schedule next event
      const nextDelay = 30000 + Math.random() * 30000; // 30-60 seconds
      this.mockEventTimer = setTimeout(emitMockEvent, nextDelay);
    };

    // Start after initial delay
    this.mockEventTimer = setTimeout(emitMockEvent, 45000);
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, Math.min(delay, 30000)); // Cap at 30 seconds
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.mockEventTimer) {
      clearTimeout(this.mockEventTimer);
      this.mockEventTimer = null;
    }
  }

  private notifyHandlers(event: RealtimeEvent): void {
    this.handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in realtime event handler:', error);
      }
    });
  }

  private statusHandlers: Set<(status: RealtimeStatus) => void> = new Set();

  onStatusChange(handler: (status: RealtimeStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  private notifyStatusChange(): void {
    this.statusHandlers.forEach((handler) => handler(this.status));
  }
}

// Singleton instance
let realtimeManager: RealtimeManager | null = null;

export function getRealtimeManager(config?: RealtimeConfig): RealtimeManager {
  if (!realtimeManager) {
    realtimeManager = new RealtimeManager(config);
  }
  return realtimeManager;
}

// React Hooks

/**
 * Hook to manage real-time connection and cache invalidation
 */
export function useRealtime(config?: RealtimeConfig) {
  const queryClient = useQueryClient();
  const managerRef = useRef<RealtimeManager | null>(null);

  useEffect(() => {
    const manager = getRealtimeManager(config);
    managerRef.current = manager;

    // Handle realtime events
    const unsubscribe = manager.subscribe((event) => {
      switch (event.type) {
        case 'decision.created':
        case 'decision.updated':
        case 'decision.deleted':
          // Invalidate decisions queries
          queryClient.invalidateQueries({ queryKey: decisionKeys.lists() });
          queryClient.invalidateQueries({ queryKey: decisionKeys.stats() });
          break;

        case 'matter.updated':
        case 'matter.stage_changed':
          // Invalidate matters queries
          queryClient.invalidateQueries({ queryKey: matterKeys.lists() });
          if (event.payload && typeof event.payload === 'object' && 'id' in event.payload) {
            queryClient.invalidateQueries({
              queryKey: matterKeys.detail(event.payload.id as string),
            });
          }
          break;

        case 'briefing.generated':
          // Invalidate briefing queries
          queryClient.invalidateQueries({ queryKey: briefingKeys.daily() });
          break;

        case 'sync':
          // Full sync - invalidate all queries
          queryClient.invalidateQueries();
          break;
      }
    });

    // Connect
    manager.connect();

    return () => {
      unsubscribe();
    };
  }, [queryClient, config]);

  const disconnect = useCallback(() => {
    managerRef.current?.disconnect();
  }, []);

  return { disconnect };
}

/**
 * Hook to subscribe to specific realtime events
 */
export function useRealtimeEvent<T = unknown>(
  eventType: RealtimeEventType | RealtimeEventType[],
  handler: (payload: T) => void
) {
  useEffect(() => {
    const manager = getRealtimeManager();
    const types = Array.isArray(eventType) ? eventType : [eventType];

    const unsubscribe = manager.subscribe((event) => {
      if (types.includes(event.type)) {
        handler(event.payload as T);
      }
    });

    return unsubscribe;
  }, [eventType, handler]);
}

/**
 * Hook to get realtime connection status
 */
export function useRealtimeStatus() {
  const [status, setStatus] = useState<RealtimeStatus>('disconnected');

  useEffect(() => {
    const manager = getRealtimeManager();
    setStatus(manager.getStatus());

    const unsubscribe = manager.onStatusChange(setStatus);
    return unsubscribe;
  }, []);

  return status;
}

export default {
  useRealtime,
  useRealtimeEvent,
  useRealtimeStatus,
  getRealtimeManager,
};
